import { useMemo, type ReactNode } from "react";
import { LocalityCard } from "@components/molecules";
import type { Locality } from "@/types";

/** Ancla del scroll suave desde el botón "Comprar entradas" (RF-8). */
export const LOCALITY_SECTION_ID = "seleccion-tickets";

interface LocalitySelectionProps {
  localities: Locality[];
  /** `maxventadigital` del evento: tope de entradas por compra. */
  maxPerPurchase: number;
  selections: Record<string, number>;
  onChange: (localityId: string, qty: number) => void;
  onPickSeats?: (localityId: number) => void;
  /** Columna derecha de la sección, a la par de las localidades: el resumen de compra.
   *  Solo desde `xl`; por debajo no hay ancho para dos columnas y manda la barra fija. */
  aside?: ReactNode;
}

/**
 * seleccion-localidades-inline RF-1 — la selección de localidades vive en la
 * página de detalle, no en un modal. Sustituye a `TicketSelectionModal`.
 */
export function LocalitySelection({
  localities,
  maxPerPurchase,
  selections,
  onChange,
  onPickSeats,
  aside,
}: LocalitySelectionProps) {
  const totalTickets = useMemo(
    () => Object.values(selections).reduce((sum, qty) => sum + qty, 0),
    [selections],
  );

  if (localities.length === 0) return null;

  return (
    <section
      id={LOCALITY_SECTION_ID}
      className="flex flex-col gap-4 pb-8 scroll-mt-24 border-b border-white/30"
    >
      <div className="flex flex-col gap-3">
        <span className="font-semibold uppercase tracking-wider text-neutral-30" style={{ fontSize: 14 }}>
          Selección de tickets
        </span>
        <div className="h-px w-full bg-white/30" />
      </div>

      {totalTickets > 0 && (
        <p className="text-white/70" style={{ fontSize: 12 }}>
          Solo puedes comprar una localidad por compra. Para elegir otra, baja la actual a 0.
        </p>
      )}

      {/* items-stretch: el panel termina a la misma altura que la última tarjeta. */}
      <div className="flex items-stretch gap-6">
        {/* Columna izquierda: las localidades */}
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:gap-3">
          {localities.map((loc) => {
            const current = selections[loc.id] ?? 0;
            // Una localidad por compra: si OTRA ya tiene entradas, esta queda bloqueada.
            const locked = totalTickets - current > 0;
            const max = Math.min(
              loc.available,
              loc.max,
              maxPerPurchase - totalTickets + current,
            );

            return (
              <LocalityCard
                key={loc.id}
                locality={loc}
                quantity={current}
                max={max}
                locked={locked}
                onChange={(qty) => onChange(String(loc.id), qty)}
                onPickSeats={onPickSeats}
              />
            );
          })}
        </div>

        {/* Columna derecha: el resumen de compra, pegado al hacer scroll */}
        {aside && (
          <div className="hidden min-[1400px]:block min-[1400px]:w-[290px] min-[1400px]:shrink-0">
            {aside}
          </div>
        )}
      </div>
    </section>
  );
}
